import React, {useEffect} from "react"
import {useTranslation} from "react-i18next"

import {
    IonAccordion, IonCheckbox,
    IonDatetime, IonDatetimeButton,
    IonInput, IonItem, IonLabel, IonModal, IonSelect, IonSelectOption,
} from "@ionic/react"

import {useTimeLimits as useItems} from "../../../contexts/TimeLimitContext"

import TimeLimit from "@customTypes/TimeLimit";
import LockingSystemZone from "@customTypes/LockingSystemZone";
import LockingSystemZoneApi from "../../../services/LockingSystemZoneApi";


interface componentProps {
    formData:          TimeLimit
    setFormData:       React.Dispatch<React.SetStateAction<TimeLimit>>
    updateFormHandler: any
}

export const AccordionTimeLimit: React.FC<componentProps> = ({
    formData, setFormData, updateFormHandler
}) => {

    const {t} = useTranslation()
    const {inProgress} = useItems()

    const [hasStartDate, setHasStartDate] = React.useState<boolean>(formData.startDate !== "" && formData.startDate !== null)
    const [hasEndDate, setHasEndDate] = React.useState<boolean>(formData.endDate !== "" && formData.endDate !== null)

    const [lockingSystemZones, setLockingSystemZones] = React.useState<LockingSystemZone[]>([])

    React.useEffect(() => {
        const fetchLockingSystemZones = async () => {
            if (lockingSystemZones.length === 0) {
                setLockingSystemZones(await LockingSystemZoneApi.getAll())
            }
        }

        fetchLockingSystemZones()
    }, [])

    useEffect(() => {
        if (lockingSystemZones.length && formData.lockingSystemZoneId === 0) {
            let foundDefaultZoneForOther: LockingSystemZone | undefined

            foundDefaultZoneForOther = lockingSystemZones.find(
                (zone) => zone.isDefaultForOther
            )

            if (foundDefaultZoneForOther) {
                setFormData({ ...formData, lockingSystemZoneId: foundDefaultZoneForOther.id })
            }
        }

    }, [lockingSystemZones])

    return (
        <IonAccordion toggleIconSlot="start" value="timeLimit">
            <IonItem className="bc-accordion" slot="header">
                <IonLabel>{t('time-limits.title_item')}</IonLabel>
            </IonItem>

            <IonItem slot="content">
                <IonSelect okText={t('common.select') as string}
                           cancelText={t('common.cancel') as string}
                           label={t('common.select') + ' ' + t('locking-system-zones.title_item')}
                           labelPlacement="floating"
                           value={formData.lockingSystemZoneId}
                           tabIndex={0}
                           interfaceOptions={{cssClass: 'alert-select-width-90'}}
                           onIonChange={updateFormHandler('lockingSystemZoneId')}
                           required>
                    {lockingSystemZones.length && lockingSystemZones.map((lockingSystemZone) => (
                        <IonSelectOption key={lockingSystemZone.id} value={lockingSystemZone.id}>
                            {lockingSystemZone.name +
                                (lockingSystemZone.isDefaultForOther
                                    ? ' (' + t('locking-system-zones.form.default_zone') + ')' : '')
                            }
                        </IonSelectOption>
                    ))}
                </IonSelect>
            </IonItem>

            <IonItem slot="content">
                <IonInput name="name" type="text"
                          label={t('time-limits.form.name') as string} labelPlacement="stacked"
                          value={formData.name}
                          minlength={2} maxlength={128}
                          placeholder={t('time-limits.form.name') as string}
                          clearInput={true}
                          autofocus={true}
                          required
                          disabled={inProgress}
                          tabIndex={1}
                          onIonChange={updateFormHandler('name')} />
            </IonItem>
            <IonItem slot="content">
                <IonInput name="description" type="text"
                          label={t('time-limits.form.description') as string} labelPlacement="stacked"
                          value={formData.description}
                          maxlength={128}
                          placeholder={t('time-limits.form.description') as string}
                          clearInput={true}
                          disabled={inProgress}
                          tabIndex={2}
                          onIonChange={updateFormHandler('description')}
                />
            </IonItem>
            <IonItem slot="content">
                <IonCheckbox labelPlacement="end" name="hasStartDate" value="1" checked={hasStartDate}
                             onClick={(ev: any) => {
                                 ev.preventDefault()
                                 ev.stopPropagation()

                                 setFormData({...formData, startDate: hasStartDate ? "" : new Date().toISOString()})

                                 setHasStartDate(! hasStartDate)
                             }}>
                    {t('time-limits.form.start_date') as string}
                </IonCheckbox>
                {hasStartDate ? (
                    <>
                        <IonDatetimeButton slot="end" datetime="startDate" />
                        <IonModal keepContentsMounted={true}>
                        <IonDatetime id="startDate" minuteValues="0,15,30,45"
                                     onIonChange={(ev) => {
                                         setFormData({...formData, startDate: ev.target.value as string})
                                     }}
                                     value={formData.startDate ? formData.startDate : null}>
                        </IonDatetime>
                        </IonModal>
                    </>
                ) : (
                    <span className="ion-padding-end">-</span>
                )}
            </IonItem>
            <IonItem slot="content">
                <IonCheckbox labelPlacement="end" name="hasEndDate" value="1"
                             checked={hasEndDate}
                             onClick={(ev) => {
                                 ev.preventDefault()
                                 ev.stopPropagation()

                                 setFormData({...formData, endDate: hasEndDate
                                         ? ""
                                         : new Date(Date.now() + 3600000).toISOString()
                                 })

                                 setHasEndDate(! hasEndDate)
                             }}>
                    {t('time-limits.form.end_date') as string}
                </IonCheckbox>
                {hasEndDate ? (
                    <>
                        <IonDatetimeButton slot="end" datetime="endDate" />
                        <IonModal keepContentsMounted={true}>
                            <IonDatetime id="endDate" minuteValues="0,15,30,45"
                                         onIonChange={(ev) => {
                                             setFormData({...formData, endDate: ev.target.value as string})
                                         }}
                                         value={formData.endDate ? formData.endDate : null} />
                        </IonModal>
                    </>
                ) : (
                    <span className="ion-padding-end">-</span>
                )}
            </IonItem>
        </IonAccordion>
    )
}

export default AccordionTimeLimit



